import { reportSpecSchema } from '@clockwise/semantic-core';
import { createFileRoute } from '@tanstack/react-router';
import { useMemo } from 'react';
import { NoAccess } from '@/components/custom/no-access';
import { PageShell } from '@/components/layout/page-shell';
import { ReportWorkbench } from '@/components/reports/report-workbench';
import { useMe } from '@/lib/me';
import { takeSpec } from '@/lib/report-handoff';

/**
 * Build a custom report — `/reports/new`.
 *
 * The vocabulary comes from `GET /api/analytics/models` and every run is a real
 * query against the warehouse. The catalog is the model with its physical half
 * projected away: no SQL reaches the browser except through the disclosure
 * below, which the server gates separately.
 *
 * Row security is the REPORT's, not this page's and not the API module's — each
 * report's SQL says what a `report.view` scope buys, so a scoped caller gets
 * fewer rows rather than a different endpoint. A caller whose scope the report
 * does not recognise gets none, which is why an empty report is a legible state
 * here rather than an error.
 *
 * Saving is in the page header, beside the `⋯` a saved report's Share and
 * Delete appear in — one row for everything you can do to one report. A save
 * moves the reader to `/reports/custom/$reportId`: from that point there is a report
 * rather than a draft, and the second Save should update it rather than make
 * another.
 */
export const Route = createFileRoute('/_authenticated/reports/new')({
  component: NewReportPage,
  /**
   * `?sql=true` shows the statement the server compiled, under the report.
   *
   * A URL PARAM rather than a control on the page, and deliberately: this is a
   * diagnostic for the people building the semantic layer, not a feature of the
   * builder, and it is expected to be removed once the layer settles. A param
   * costs nothing to carry and nothing to take away, whereas a button has to be
   * designed, explained and then un-shipped.
   *
   * The server still decides. `?sql=true` from a caller without `report.share`
   * is a 403, not a quietly plain result — see `AnalyticsService.run`.
   */
  // OPTIONAL in the parsed shape, and absent rather than `false` when it is
  // off. A required key would make every `<Link>` and `navigate()` to this
  // route carry `search: { sql: false }`, which is a lot of ceremony for a
  // diagnostic — and would put `?sql=false` in the URL of everyone who never
  // asked about SQL.
  //
  // `?from=` names a spec parked by a drill's "Open as its own report" (see
  // `report-handoff.ts`) — a nonce, never the spec itself.
  validateSearch: (search: Record<string, unknown>): { sql?: true; from?: string } => ({
    // Only the literal string. `Boolean('false')` is `true`, which would make
    // `?sql=false` turn the disclosure ON.
    ...(search.sql === 'true' || search.sql === true ? { sql: true as const } : {}),
    ...(typeof search.from === 'string' && search.from ? { from: search.from } : {}),
  }),
});

function NewReportPage() {
  const { sql: includeSql = false, from } = Route.useSearch();
  // Parsed, not trusted: it came through browser storage.
  const initialSpec = useMemo(() => {
    const parsed = reportSpecSchema.safeParse(takeSpec(from));
    return parsed.success ? parsed.data : undefined;
  }, [from]);
  // `report.edit` ON TOP of the layout's `report.view`. The layout gate is what
  // makes the Reports section visible at all; this one is the builder, and both
  // analytics endpoints refuse a caller without it. Gating here as well means
  // that refusal reads as "you don't have this" rather than as a broken page.
  const { permissions, loading } = useMe();
  const mayBuild = !loading && permissions.has('report.edit');

  if (!loading && !mayBuild) {
    return <NoAccess permission="report.edit" resource="the report builder" />;
  }

  return (
    <PageShell>
      <ReportWorkbench
        // A handed-over spec is a NEW draft: remount rather than hand the
        // builder a prop it is entitled to ignore once it owns the draft.
        key={from ?? 'blank'}
        initialSpec={initialSpec}
        // Both stand in only until the draft has a name and a description of
        // its own — the heading becomes the report's name the moment it is
        // given one, which is where the reader typed it.
        fallbackTitle="Build a report"
        fallbackSubtitle="Pick your measures, group them across two axes, and choose how to show the result."
        includeSql={includeSql}
        mayEdit={mayBuild}
      />
    </PageShell>
  );
}
